import {FormSessionDto, FormSessionFilterDto} from "../../client/formsession/FormSessionApiClient";
import {FormSessionTranslKey} from "./FormSessionTranslKey";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {formatUserLastNameFirst} from "../user/UserFormatter";
import {DateTimeFormatter} from "../../common/DateTimeFormatter";
import {GridColDef} from "@mui/x-data-grid-pro";
import {useCatalog} from "../../common/catalog/UseCatalog";
import {CatalogType} from "../../client/catalog/CatalogType";
import PageableGrid, {PageLoadSize} from "../../common/pagination/PageableGrid";
import {FilterSearchType} from "./search/FormSessionSearchPanel";
import {TFunction} from "i18next";

function buildFormSessionGridColumns(documentTypeCatalogError: String | null, t: TFunction): GridColDef[] {
    return [
        {
            field: "createdAt",
            type: "dateTime",
            headerName: t(FormSessionTranslKey.ACCESSED_AT),
            width: 200,
            valueGetter: params => params.value ? new Date(params.value) : undefined,
            valueFormatter: params => DateTimeFormatter.formatDateTime(params.value)
        },
        {
            field: "documentType",
            headerName: t(FormSessionTranslKey.DOCUMENT_TYPE),
            width: 300,
            valueGetter: params => {
                if (documentTypeCatalogError) {
                    return '';
                } else {
                    return t(params.value, {ns: CatalogType.DOCUMENT_TYPE}) || '';

                }
            }
        },
        {
            field: "documentVersion",
            headerName: t(FormSessionTranslKey.DOCUMENT_VERSION),
            width: 200,
        },
        {
            field: "user",
            headerName: t(FormSessionTranslKey.USER),
            width: 200,
            valueGetter: params => formatUserLastNameFirst(params.row.user)
        },
        {
            field: "email",
            headerName: t(FormSessionTranslKey.EMAIL),
            width: 200,
            valueGetter: params => params.row.user?.person?.email,
        },
        {
            field: "phone",
            headerName: t(FormSessionTranslKey.PHONE),
            width: 150,
            valueGetter: params => params.row.user?.person?.phone,
        },
        {
            field: "datovaSchranka",
            headerName: t(FormSessionTranslKey.DATOVA_SCHRANKA),
            width: 150,
            valueGetter: params => params.row.user?.person?.databox,
        },
    ];
}

export interface FormSessionGridProps {
    rowsData: FormSessionDto[];
    totalDataCount: number;
    nextPageToken?: string | null;

    formSessionFilter: FormSessionFilterDto;
    fetchFormSession: (filter: FormSessionFilterDto, searchType: FilterSearchType) => void;

    selectedFormSessionId?: number;
    onSelectFormSession?: (formSessionDto: FormSessionDto) => void;
    onFormSessionDoubleClick?: (formSessionId: number) => void;
}

export default function FormSessionGrid(props: Readonly<FormSessionGridProps>) {
    const {rowsData, totalDataCount, selectedFormSessionId, nextPageToken, formSessionFilter, fetchFormSession, onSelectFormSession, onFormSessionDoubleClick} = props;

    const {t} = useTranslation();
    const {isLoading: catalogLoading, error: catalogError} = useCatalog(CatalogType.DOCUMENT_TYPE);
    const columns = useMemo(() => buildFormSessionGridColumns(catalogError, t), [catalogError, t]);

    const handleFetchMore = useCallback(() => {
        if (!nextPageToken) {
            return;
        }

        const nextPageFilter: FormSessionFilterDto = {
            ...formSessionFilter,
            paging: {
                maxPageSize: PageLoadSize.NEXT_PAGE_SIZE,
                pageToken: nextPageToken,
            },
        };

        fetchFormSession(nextPageFilter, FilterSearchType.NEXT_PAGE_SEARCH);
    }, [nextPageToken, fetchFormSession, formSessionFilter]);

    return (
        <PageableGrid
            rowsData={rowsData}
            totalRowsCount={totalDataCount}
            columns={columns}
            catalogLoading={catalogLoading}
            onFetchMore={handleFetchMore}
            selectedRowId={selectedFormSessionId}
            onRowSelect={onSelectFormSession}
            onRowDoubleClick={onFormSessionDoubleClick}
        />
    );
}
